import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import {
  usePermissions,
  useHasPermission,
} from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import * as React from 'react';

import type {
  UpcomingReservationWidgetMFEProps,
  UpcomingReservationWidgetProps,
} from './UpcomingReservationWidget.types';
const UpcomingReservationWidget = React.memo(
  ({
    onLoad,
  }: Readonly<UpcomingReservationWidgetProps>): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const canViewUpcomingReservationWidget = useHasPermission(
      'ModuleTrain_View_Any'
    );

    const [RemoteComponent, setRemoteComponent] =
      React.useState<React.ComponentType<UpcomingReservationWidgetMFEProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      if (!canViewUpcomingReservationWidget) {
        onLoad?.();
        return;
      }

      const loadComponent = async (): Promise<void> => {
        try {
          const { default: Component } =
            await import('learning_mfe/UpcomingReservationWidget');
          setRemoteComponent(() => Component);
        } catch (error) {
          console.error('Error loading UpcomingReservationWidget:', error);
          setLoadError(error as Error);
          onLoad?.();
        }
      };

      loadComponent();
    }, [canViewUpcomingReservationWidget, onLoad]);

    if (!canViewUpcomingReservationWidget || loadError) {
      return null;
    }

    if (!RemoteComponent || !authData) {
      return null;
    }

    return (
      <React.Suspense
        fallback={
          <ContentLoader data-testid="UpcomingReservationWidget-content-loader" />
        }
      >
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          permissions={permissions}
          onLoad={onLoad}
        />
      </React.Suspense>
    );
  }
);

UpcomingReservationWidget.displayName = 'UpcomingReservationWidget';

export { UpcomingReservationWidget };
